/* Sparkles (sections/cs-sparkles.liquid)
   Reads the settings from [data-sparkles] and, for every alt-font group in
   a headline on the page (the bold words of an alt-font headline, or a
   .font-alt element in one),
   wraps two letters in .cs-sparkle-letter and adds a sparkle to each:
   - the first letter from the Top-right letters: sparkle at its top right
   - the last letter from the Bottom-left letters: sparkle at its bottom left
     (skipped when it's the same letter)
   Letters are matched as they show on the page (text-transform included).
   The sparkle image is white, so a group whose nearest background is a
   light color (no photo or video behind it) gets .is-on-light, which hides
   its sparkles; checked again when the screen crosses 750px, where layouts
   like a stacked hero change what's behind the text.
   Runs again when a section is edited in the theme editor.
*/

(() => {
    const GROUPS = '.font-alt-bold strong, .font-alt-bold b, .font-alt';
    // Headlines only, not small alt-font labels
    const HEADLINES = 'h1, h2, h3, h4, h5, h6, .display, .h1, .h2, .h3, .headline-md, .headline-sm';

    const settings = () => {
        const config = document.querySelector('[data-sparkles]');
        if (!config) return null;
        const letters = (value) => new Set((value || '').replace(/[^A-Z]/g, ''));
        return {
            image: config.dataset.image,
            top: letters(config.dataset.top),
            bottom: letters(config.dataset.bottom),
            sizes: (config.dataset.sizes || '0.5').split(',').map(Number),
        };
    };

    const random = (min, max) => min + Math.random() * (max - min);

    // True when the first thing painted behind the element is a near-white
    // color (white, light grays), not a photo, video or background image.
    // Pastels like the lilac scheme still show the sparkles.
    const isOnLight = (element) => {
        const box = element.getBoundingClientRect();
        // A photo or video only counts when it's behind the text, not beside
        // it (e.g. Image with text)
        const isBehind = (media) => {
            const r = media.getBoundingClientRect();
            return r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;
        };
        for (let el = element; el && el !== document.documentElement; el = el.parentElement) {
            const style = getComputedStyle(el);
            if (style.backgroundImage !== 'none') return false;
            const media = el.querySelectorAll(':scope > img, :scope > picture, :scope > video, :scope > [class*="__media"]');
            if ([...media].some(isBehind)) return false;
            const [r, g, b, a = 1] = (style.backgroundColor.match(/[\d.]+/g) || []).map(Number);
            if (a === 0 || r === undefined) continue;
            // Relative luminance, 0 (black) to 1 (white)
            const lum = (channel) => {
                const c = channel / 255;
                return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
            };
            return 0.2126 * lum(r) + 0.7152 * lum(g) + 0.0722 * lum(b) > 0.8;
        }
        return true;
    };

    const checkBackgrounds = () => {
        document.querySelectorAll('[data-sparkled]').forEach((group) => {
            group.classList.toggle('is-on-light', isOnLight(group));
        });
    };

    // Each letter of the group, in order, with its text node and how it shows
    const lettersOf = (group) => {
        const letters = [];
        const walker = document.createTreeWalker(group, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
            const node = walker.currentNode;
            if (node.parentElement.closest('.cs-sparkle-letter')) continue;
            const upper = getComputedStyle(node.parentElement).textTransform === 'uppercase';
            [...node.data].forEach((char, index) => {
                letters.push({ node, index, shown: upper ? char.toUpperCase() : char });
            });
        }
        return letters;
    };

    const addSparkle = (letter, position, config) => {
        // Split the letter out of its text node and wrap it
        const after = letter.node.splitText(letter.index);
        after.splitText(1);
        const wrap = document.createElement('span');
        wrap.className = 'cs-sparkle-letter';
        after.replaceWith(wrap);
        wrap.append(after);

        const sparkle = document.createElement('span');
        sparkle.className = `cs-sparkle cs-sparkle--${position}`;
        sparkle.setAttribute('aria-hidden', 'true');
        const size = config.sizes[Math.floor(Math.random() * config.sizes.length)];
        const inDelay = random(0, 0.4);
        sparkle.style.setProperty('--cs-sparkle-size', `${size}em`);
        sparkle.style.setProperty('--cs-sparkle-in-delay', `${inDelay.toFixed(2)}s`);
        sparkle.style.setProperty('--cs-sparkle-delay', `${(inDelay + 0.7).toFixed(2)}s`);
        sparkle.style.setProperty('--cs-sparkle-speed', `${random(3.5, 6).toFixed(2)}s`);
        const image = document.createElement('img');
        image.src = config.image;
        image.alt = '';
        sparkle.append(image);
        wrap.append(sparkle);
    };

    const sparkle = (root = document) => {
        const config = settings();
        if (!config?.image) return;
        root.querySelectorAll(GROUPS).forEach((group) => {
            // One group per alt-font run: skip ones inside another, and ones done
            if (group.dataset.sparkled || group.parentElement.closest(GROUPS)) return;
            if (!group.closest(HEADLINES)) return;
            group.dataset.sparkled = 'true';

            const letters = lettersOf(group);
            const first = letters.find((letter) => config.top.has(letter.shown));
            const last = letters.findLast((letter) => config.bottom.has(letter.shown));
            // Wrap the later letter first, so the earlier one's place holds
            if (last && last !== first) addSparkle(last, 'bottom', config);
            if (first) addSparkle(first, 'top', config);
        });
        checkBackgrounds();
    };

    const start = () => {
        try {
            sparkle();
        } catch (error) {
            console.error('Error adding sparkles:', error);
        }
    };

    window.matchMedia('(min-width: 750px)').addEventListener('change', checkBackgrounds);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }

    // Theme editor: a re-rendered section gets its sparkles back
    document.addEventListener('shopify:section:load', (event) => {
        try {
            sparkle(event.target);
        } catch (error) {
            console.error('Error adding sparkles:', error);
        }
    });
})();
